import getOffersTranslationsQuery from '../queries/getOffersTranslationsQuery';
import { logError } from '../../shared/utils/logging';
import { StorefrontApiClient } from '@shopify/storefront-api-client';
import createCandyRackClient from '../client';
import { Offer } from '../../shared/types';
import getShopifyGraphQLUpperLocale from '../../shared/utils/getShopifyGraphQLUpperLocale';

export const getTranslatedOffersForCandyRack: (
  offers: Offer[],
  token: string,
) => Promise<Offer[]> = async (offers: Offer[], token: string) => {
  const storefrontApiClient = createCandyRackClient(token);
  return getTranslatedOffers(offers, storefrontApiClient) as Promise<Offer[]>;
};

/*
 * Fills the offers translations for the description and the short_description
 * using getOffersTranslationsMetaobject @inContext information
 * If nothing is done (no translations existing) the input offers array is returned as is instead
 */
const getTranslatedOffers = async (
  offers: Offer[],
  storefrontApiClient: StorefrontApiClient | null,
): Promise<Offer[]> => {
  // If the API client couldn't be created, return the original offers
  if (!storefrontApiClient) {
    // Return the original offers
    return offers;
  }

  const offersRequiringTranslation = offers.filter((offer) => {
    // Include non-product offers or those with a non-empty badge label
    if (!offer.is_product || offer.badge_label?.trim()) {
      return true;
    }

    // Otherwise, check if any item has a translatable badge or description
    return offer.items.some(
      (item) => item.item_badge_label?.trim() || item.item_description?.trim(),
    );
  });

  // If no offers need translation, return the original offers
  if (offersRequiringTranslation.length === 0) {
    return offers;
  }

  // Query for translations
  const {
    data,
    errors,
    // @ts-ignore there seems to some mismatch between type understanding and what storefrontApiClient.request accepts
  } = await storefrontApiClient.request(
    getOffersTranslationsQuery(
      offersRequiringTranslation,
      getShopifyGraphQLUpperLocale(),
    ),
  );

  if (errors) {
    logError(`Storefront API failed, using fallback, ${errors}`);
    return offers;
  }

  // If data is received, map the translations back to the offers
  if (data) {
    return offers.map((offer) => {
      if (offer.is_product) {
        // Handle product offer translations
        const translation = data[`ProductOffer${offer.id}`];
        if (translation) {
          if (offer.use_recommended_products || offer.is_same_product_offer) {
            if (offer.badge_label) {
              offer.badge_label =
                findTranslationValue(translation.fields, 'badge') ||
                offer.badge_label;
            }
          } else {
            // manual product offer
            offer.items.forEach((item, index) => {
              // item_badge_label exists only for manual products.
              if (item.item_badge_label) {
                const badgeKey = `product${(index + 1)
                  .toString()
                  .padStart(2, '0')}_badge`;
                item.item_badge_label =
                  findTranslationValue(translation.fields, badgeKey) ||
                  item.item_badge_label;
              }
              if (item.item_description) {
                const itemKey = `product${(index + 1)
                  .toString()
                  .padStart(2, '0')}_description`;
                item.item_description =
                  findTranslationValue(translation.fields, itemKey) ||
                  item.item_description;
              }
            });
          }
        }
      } else {
        const translation = data[`ServiceOffer${offer.id}`];
        if (translation) {
          offer.headline =
            findTranslationValue(translation.fields, 'title') || offer.headline;
          offer.description =
            findTranslationValue(translation.fields, 'detailed_description') ||
            offer.description;
          offer.short_description =
            findTranslationValue(translation.fields, 'description') ||
            offer.short_description;
          offer.badge_label =
            findTranslationValue(translation.fields, 'badge') ||
            offer.badge_label;
        }
      }
      return offer;
    });
  }

  return offers;
};

// Helper function to extract translation values
const findTranslationValue = (
  fields: { key: string; value: string }[],
  key: string,
): string | undefined => {
  return fields.find((field) => field.key === key)?.value;
};

// Export for testing only
export const __test__ = {
  getTranslatedOffers,
};
